import { Fragment, type ReactNode } from 'react';
import type { QuoteSettingRow } from './types';
import { Card, MicroLabel } from './ui/primitives';

export interface QuoteSettingsGroup {
	title: string;
	rows: QuoteSettingRow[];
}

/**
 * Payment settings, snapshotted from the client when the quote was created.
 *
 * Grouped rather than listed flat: "how is this collected" and "what are the
 * limits" are different questions, and the limits only make sense once the mode
 * above them is known.
 *
 * `note` carries the snapshot state — whether these values were frozen onto the
 * quote or are still the client's live ones. That is per-quote FACT, not a
 * description of the section, so it belongs in the card rather than in whatever
 * heading the page puts above it.
 */
export function QuoteSettings({
	groups,
	note,
	footer,
}: {
	groups: QuoteSettingsGroup[];
	note?: ReactNode;
	/** The save bar, when the viewer may edit. */
	footer?: ReactNode;
}) {
	return (
		<Card>
			{note && (
				<div className="border-b border-line-1 px-4 py-3 text-sm text-text-3">{note}</div>
			)}
			{groups.map((group) => (
				<Fragment key={group.title}>
					<MicroLabel className="block px-4 pt-3 pb-1">{group.title}</MicroLabel>
					{group.rows.map((row) => (
						<div
							key={row.key}
							className="flex flex-wrap items-center gap-4 border-b border-line-1 px-4 py-3 last:border-b-0"
						>
							<div className="min-w-[200px] flex-1">
								<div className="text-base font-medium text-text-1">{row.label}</div>
								{row.description && (
									<div className="mt-px text-xs text-text-4">
										{row.description}
									</div>
								)}
							</div>
							<div className="shrink-0">{row.control}</div>
						</div>
					))}
				</Fragment>
			))}
			{footer && <div className="border-t border-line-1 px-4 py-3">{footer}</div>}
		</Card>
	);
}
